import React, { useState, useRef, useContext, useEffect } from "react";
import {
  Send,
  Bot,
  User,
  AlertCircle,
  Trash2,
} from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import AppContext from "../../../../context/AppContext";
import "../../../../assets/css/customer/customerdashboard/customerAiPlanner/aiplanner.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
const QUICK_PROMPTS = [
  "Budget under PKR 25,000",
  "Hunza Valley trip plan",
  "Family trip, 5 days",
  "Solo adventure, PKR 40,000",
];

const AIPlanner = () => {

  const { authToken, baseUrl } = useContext(AppContext);

  const navigate = useNavigate();
  const location = useLocation();

  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const [inputMessage, setInputMessage] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [messages, setMessages] = useState([]);

  const hasStarted = messages.length > 0;

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (!authToken) {
      Swal.fire({
        icon: "warning",
        title: "Login Required",
        text: "Please login to use the Trip Planner.",
        confirmButtonText: "Login",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/auth/login", { state: { from: location.pathname } });
        } else {
          navigate("/");
        }
      });
    }
  }, [authToken]);

useEffect(() => {
  if (authToken) {
    loadPreviousChat();
  }
}, [authToken]);

const loadPreviousChat = () => {

  axios
    .get(`${baseUrl}/customer/aichat/history`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
    .then((response) => {

      const history = response.data.messages || [];

      const formatted = history.map((item) => ({
    id: item.id,
    type: item.sender === "customer" ? "user" : "ai",
    content: item.message,
    timestamp: item.created_at,
}));

setMessages(formatted);

    })
    .catch((err) => {
      console.log(err);
    });

};

 const callAI = () => {

  if (!inputMessage.trim() || isLoading) return;

  const messageToSend = inputMessage;

  const userMessage = {
    id: Date.now(),
    type: "user",
    content: messageToSend,
    timestamp: new Date(),
  };

  setMessages((prev) => [...prev, userMessage]);

  setInputMessage("");
  setIsLoading(true);

  const formData = new FormData();
  formData.append("message", messageToSend);

  axios
    .post(`${baseUrl}/customer/aichat`, formData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {

      const aiMessage = {
        id: Date.now() + 1,
        type: "ai",
        content:
          response.data.message ||
          response.data.reply ||
          response.data.airesponse ||
          "No response received.",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);

    })
    .catch((error) => {

      console.log(error);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: "ai",
          content: "Something went wrong.",
          isError: true,
          timestamp: new Date(),
        },
      ]);

    })
    .finally(() => {

      setIsLoading(false);

    });

};


  const clearChat = () => {
    Swal.fire({
      icon: "warning",
      title: "Clear Chat?",
      text: "This will permanently delete your conversation history.",
      showCancelButton: true,
      confirmButtonText: "Yes, clear it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios
        .delete(`${baseUrl}/customer/aichat/clear`, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then(() => {
          setMessages([]);
        })
        .catch((err) => {
          console.log(err);
          Swal.fire({
            icon: "error",
            title: "Could not clear chat",
            text: "Please try again.",
          });
        });
    });
  };

  const renderInputBar = (hero) => (
    <div
      className={`cus-ai-plan-input-bar ${
        hero ? "cus-ai-plan-input-bar-hero" : ""
      }`}
    >
      <input
        ref={hero ? inputRef : null}
        type="text"
        className="cus-ai-plan-message-input"
        placeholder="What do you want to know..."
        value={inputMessage}
        disabled={isLoading}
        onChange={(e) => setInputMessage(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            callAI();
          }
        }}
      />

      <button
        className="cus-ai-plan-send-btn"
        onClick={callAI}
        disabled={isLoading || !inputMessage.trim()}
      >
        {isLoading ? (
          <div className="cus-ai-plan-spinner" />
        ) : (
          <Send size={17} />
        )}
      </button>
    </div>
  );

  if (!authToken) {
    return null;
  }

  return (
    
    <div className="cus-ai-plan-ai-planner-container">


      <div className="cus-ai-plan-chat-main">


        <div className="cus-ai-plan-chat-header">

          <div className="cus-ai-plan-header-text">
            <h2 className="cus-ai-plan-header-title">
              Journey Junction Trip Planner
            </h2>
          </div>

          {hasStarted && (
            <button className="cus-ai-plan-clear-btn" onClick={clearChat}>
              <Trash2 size={14} />
              <span className="cus-ai-plan-clear-btn-text">Clear Chat</span>
            </button>
          )}

        </div>

        {!hasStarted ? (

          <div className="cus-ai-plan-hero">

            <h1 className="cus-ai-plan-hero-title">
              What can I do for you today?
            </h1>

            {renderInputBar(true)}

            <div className="cus-ai-plan-quick-prompts">

              {QUICK_PROMPTS.map((prompt, index) => (

                <button
                  key={index}
                  className="cus-ai-plan-quick-prompt-btn"
                  onClick={() => setInputMessage(prompt)}
                >
                  {prompt}
                </button>

              ))}

            </div>

          </div>

        ) : (          <>

            <div
              className="cus-ai-plan-messages-container"
              ref={messagesContainerRef}
            >
              <div className="cus-ai-plan-messages-inner">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`cus-ai-plan-message cus-ai-plan-${msg.type}`}
                  >
                    {msg.type === "ai" && (
                      <div className="cus-ai-plan-message-avatar cus-ai-plan-ai-avatar">
                        <Bot size={16} color="white" />
                      </div>
                    )}

                    <div className="cus-ai-plan-message-content">

                      {msg.isError ? (
                        <div className="cus-ai-plan-error-msg">
                          <AlertCircle size={15} />
                          {msg.content}
                        </div>
                      ) : (
                        <div className="cus-ai-plan-message-text">
                         <ReactMarkdown remarkPlugins={[remarkGfm]}>
                     {msg.content}
                        </ReactMarkdown>
                        </div>
                      )}

                      <div className="cus-ai-plan-message-time">
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>

                    </div>

                    {msg.type === "user" && (
                      <div className="cus-ai-plan-message-avatar cus-ai-plan-user-avatar">
                        <User size={16} color="white" />
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="cus-ai-plan-typing">

                    <div className="cus-ai-plan-message-avatar cus-ai-plan-ai-avatar">
                      <Bot size={16} color="white" />
                    </div>

                    <div className="cus-ai-plan-typing-bubble">
                      <div className="cus-ai-plan-typing-dot"></div>
                      <div className="cus-ai-plan-typing-dot"></div>
                      <div className="cus-ai-plan-typing-dot"></div>
                    </div>

                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>


            <div className="cus-ai-plan-input-container">
              {renderInputBar(false)}
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default AIPlanner;